import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./dialog";
import { Button } from "./button";
import { toast } from "sonner";
import { formatCPF, isValidCPF } from "../../lib/utils";

const WPP_ICON = (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
  </svg>
);

const MAX_MSG = 140;

export function WhatsAppSupportDialog({ isOpen, onOpenChange, cpf, dataNascimento = "", errorType = "not_found", phone }) {
  const [nome, setNome] = useState("");
  const [cpfInput, setCpfInput] = useState("");
  const [mensagem, setMensagem] = useState("");

  const effectiveCpf = cpf || cpfInput.trim();
  const cpfFilled = !cpf && cpfInput.replace(/\D/g, "").length > 0;
  const cpfError = cpfFilled && !isValidCPF(cpfInput);
  const remaining = MAX_MSG - mensagem.length;
  const canSend = nome.trim().length >= 2 && mensagem.trim().length > 0 && !!phone && !cpfError;

  const handleSend = () => {
    const dobLine = dataNascimento ? `\nData de Nascimento: ${dataNascimento}` : "";
    let text = "";
    if (errorType === "valor_zero") {
      text = encodeURIComponent(
        `Olá! Gostaria de tirar dúvidas sobre minha proposta.\nNome: ${nome.trim()}\nCPF: ${effectiveCpf || "(não informado)"}${dobLine}\nMensagem: ${mensagem.trim()}\n*VP-01*`
      );
    } else {
      text = encodeURIComponent(
        `Olá! Preciso de suporte para acesso ao portal.\nNome: ${nome.trim()}\nCPF: ${effectiveCpf || "(não informado)"}${dobLine}\nMensagem: ${mensagem.trim()}`
      );
    }
    
    window.open(`https://wa.me/55${phone}?text=${text}`, "_blank");
    toast.success("Redirecionando para o WhatsApp...");
    setNome("");
    setCpfInput("");
    setMensagem("");
    onOpenChange(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-card">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#128C7E]">
            {WPP_ICON}
            Falar com Suporte
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {cpf ? (
            <div className="space-y-2">
              {errorType === "dob_mismatch" ? (
                <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-800 space-y-1">
                  <p>A data de nascimento informada não confere com o CPF <span className="font-mono font-semibold">{cpf}</span>.</p>
                  {dataNascimento && (
                    <p className="text-xs text-amber-700">Data informada: <span className="font-mono font-semibold">{dataNascimento}</span></p>
                  )}
                </div>
              ) : errorType === "valor_zero" ? (
                <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-800 space-y-1">
                  <p>Seu CPF <span className="font-mono font-semibold">{cpf}</span> foi localizado, mas sua proposta ainda está sendo processada.</p>
                  <p className="text-xs text-amber-700">Entre em contato com o suporte para mais informações.</p>
                </div>
              ) : (
                <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700">
                  O CPF <span className="font-mono font-semibold">{cpf}</span> não foi encontrado na base de dados.
                </div>
              )}
              <p className="text-xs text-slate-500">Entre em contato com o suporte para solicitar seu cadastro ou verificar seus dados.</p>
            </div>
          ) : (
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-slate-700">
                CPF <span className="text-slate-400 text-xs font-normal">(opcional)</span>
              </label>
              <input
                type="text"
                value={cpfInput}
                onChange={e => setCpfInput(formatCPF(e.target.value))}
                placeholder="000.000.000-00"
                maxLength={14}
                className={`w-full border rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 ${
                  cpfError
                    ? "border-red-400 focus:ring-red-300/30 focus:border-red-400"
                    : "border-slate-200 focus:ring-[#25D366]/30 focus:border-[#25D366]/40"
                }`}
              />
              {cpfError && (
                <p className="text-xs text-red-500">CPF inválido</p>
              )}
            </div>
          )}

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-slate-700">
              Nome <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={nome}
              onChange={e => setNome(e.target.value)}
              placeholder="Seu nome completo"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#25D366]/30 focus:border-[#25D366]/40"
            />
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-slate-700">
                Mensagem <span className="text-red-500">*</span>
              </label>
              <span className={`text-xs font-mono ${remaining <= 10 ? "text-red-500" : "text-slate-400"}`}>
                {remaining} restantes
              </span>
            </div>
            <textarea
              value={mensagem}
              onChange={e => setMensagem(e.target.value.slice(0, MAX_MSG))}
              placeholder="Descreva sua dúvida..."
              rows={3}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#25D366]/30 focus:border-[#25D366]/40 resize-none"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleSend}
            disabled={!canSend}
            className="bg-[#25D366] hover:bg-[#20bd5a] text-white gap-2"
          >
            {WPP_ICON}
            Enviar via WhatsApp
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
